import * as THREE from 'three'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js'
import maplibregl from 'maplibre-gl'
import type { CustomLayer } from '../model/types'
import {
  MODEL_PATH,
  FLOOR_COLOR,
  HIGHLIGHT_COLOR,
  FLOOR_LAYER_MAP,
} from '@/components/shared/map/model-viewer/model/constants'
import { enhanceBuildingMaterials } from '@/components/shared/map/model-viewer/lib/mesh-utils'
import { addBalancedSceneLights, SCENE_LIGHTING } from '@/components/shared/map/model-viewer/model/scene-lighting'

const BUILDING_COORDS: [number, number] = [30.323453, 59.890131]

const isFloorMesh = (obj: THREE.Object3D): obj is THREE.Mesh =>
  obj instanceof THREE.Mesh && obj.name.toLowerCase().includes('floor')

/** Наведение только без зажатых кнопок — не мешает pan/rotate карты. */
const isHoverPointerEvent = (event: MouseEvent) => event.buttons === 0

/** Клик по этажу только ЛКМ. */
const isLeftClickEvent = (event: MouseEvent) => event.button === 0

interface BuildingLayerOptions {
  /** Множитель размера модели (1 = в метрах по карте). */
  scale?: number
  /** Поворот вокруг вертикальной оси, радианы (положительный = против часовой). */
  rotation?: number
  onFloorClick?: (layerId: number) => void
}

export function createBuildingLayer(
  options: BuildingLayerOptions = {},
): CustomLayer {
  const { scale = 1, rotation = 0, onFloorClick } = options

  const mercator = maplibregl.MercatorCoordinate.fromLngLat(BUILDING_COORDS, 0)
  const meterScale = mercator.meterInMercatorCoordinateUnits()

  let camera: THREE.PerspectiveCamera
  let scene: THREE.Scene
  let renderer: THREE.WebGLRenderer
  let map: maplibregl.Map
  let raycaster: THREE.Raycaster
  let hoveredMesh: THREE.Mesh | null = null
  let modelLoaded = false
  let lastProjectionMatrix: THREE.Matrix4 | null = null
  const floorMeshes: THREE.Mesh[] = []
  const floorMaterials = new Map<THREE.Mesh, THREE.MeshStandardMaterial>()
  let handleMouseMove: ((e: maplibregl.MapMouseEvent) => void) | null = null
  let handleClick: ((e: maplibregl.MapMouseEvent) => void) | null = null
  let handleContextMenu: ((e: MouseEvent) => void) | null = null

  /** NDC [-1,1] из события мыши с учётом размера буфера canvas (devicePixelRatio). */
  const eventToNDC = (e: MouseEvent, canvas: HTMLCanvasElement) => {
    const rect = canvas.getBoundingClientRect()
    const x = ((e.clientX - rect.left) * canvas.width) / rect.width
    const y = ((e.clientY - rect.top) * canvas.height) / rect.height
    return new THREE.Vector2(
      (x / canvas.width) * 2 - 1,
      -(y / canvas.height) * 2 + 1,
    )
  }

  /**
   * Строит луч для raycast при кастомной проекции (MapLibre).
   * Луч задаётся двумя точками (near/far в NDC), чтобы направление
   * было стабильным под любым углом обзора.
   */
  const setRayFromProjection = (
    raycaster: THREE.Raycaster,
    mouseNDC: THREE.Vector2,
    projectionMatrixInverse: THREE.Matrix4,
  ) => {
    const near = new THREE.Vector3(mouseNDC.x, mouseNDC.y, -1).applyMatrix4(
      projectionMatrixInverse,
    )
    const far = new THREE.Vector3(mouseNDC.x, mouseNDC.y, 1).applyMatrix4(
      projectionMatrixInverse,
    )
    const dir = far.sub(near).normalize()
    raycaster.ray.set(near, dir)
  }

  return {
    id: 'building-3d-model',
    type: 'custom',
    renderingMode: '3d',

    onAdd(mapInstance, gl) {
      map = mapInstance
      camera = new THREE.PerspectiveCamera(50, 1, 0.1, 10000)
      scene = new THREE.Scene()
      raycaster = new THREE.Raycaster()

      addBalancedSceneLights(scene)

      const dracoLoader = new DRACOLoader()
      dracoLoader.setDecoderPath(
        'https://www.gstatic.com/draco/versioned/decoders/1.5.7/',
      )

      const loader = new GLTFLoader()
      loader.setDRACOLoader(dracoLoader)
      loader.load(MODEL_PATH, gltf => {
        enhanceBuildingMaterials(gltf.scene)

        gltf.scene.traverse(child => {
          if (!isFloorMesh(child)) return

          const material = new THREE.MeshStandardMaterial({
            color: FLOOR_COLOR,
            transparent: true,
            opacity: 0,
          })
          child.material = material
          floorMeshes.push(child)
          floorMaterials.set(child, material)
        })

        scene.add(gltf.scene)
        modelLoaded = true
        map.triggerRepaint()
      })

      renderer = new THREE.WebGLRenderer({
        canvas: map.getCanvas(),
        context: gl,
        antialias: true,
      })
      renderer.outputColorSpace = THREE.SRGBColorSpace
      renderer.toneMapping = THREE.ACESFilmicToneMapping
      renderer.toneMappingExposure = SCENE_LIGHTING.exposure
      renderer.autoClear = false

      const updateRaycast = () => {
        if (!modelLoaded || !lastProjectionMatrix) return
        camera.projectionMatrix.copy(lastProjectionMatrix)
        camera.projectionMatrixInverse.copy(lastProjectionMatrix).invert()
        camera.updateMatrixWorld()
        scene.updateMatrixWorld(true)
      }

      handleContextMenu = (e: MouseEvent) => {
        e.preventDefault()
      }
      map.getCanvas().addEventListener('contextmenu', handleContextMenu)

      handleMouseMove = (e: maplibregl.MapMouseEvent) => {
        const mouseEvent = e.originalEvent as MouseEvent
        if (!isHoverPointerEvent(mouseEvent)) return
        if (!modelLoaded || !lastProjectionMatrix) return
        updateRaycast()

        const canvas = map.getCanvas()
        const mouse = eventToNDC(mouseEvent, canvas)
        setRayFromProjection(raycaster, mouse, camera.projectionMatrixInverse)
        const intersects = raycaster.intersectObjects(floorMeshes, false)
        const floorHit = intersects[0]?.object as THREE.Mesh | undefined

        const needsUpdate = hoveredMesh !== floorHit

        if (needsUpdate) {
          hoveredMesh = floorHit ?? null

          if (hoveredMesh) {
            map.getCanvas().style.cursor = 'pointer'
          } else {
            map.getCanvas().style.cursor = ''
          }

          map.triggerRepaint()
        }
      }

      map.on('mousemove', handleMouseMove)

      handleClick = (e: maplibregl.MapMouseEvent) => {
        const mouseEvent = e.originalEvent as MouseEvent
        if (!isLeftClickEvent(mouseEvent)) return
        if (!modelLoaded || !lastProjectionMatrix) return
        updateRaycast()

        const canvas = map.getCanvas()
        const mouse = eventToNDC(mouseEvent, canvas)
        setRayFromProjection(raycaster, mouse, camera.projectionMatrixInverse)
        const intersects = raycaster.intersectObjects(scene.children, true)
        const floorHit = intersects.find(i => isFloorMesh(i.object))

        if (floorHit && onFloorClick) {
          const name = floorHit.object.name.toLowerCase()
          const layerId = FLOOR_LAYER_MAP[name]
          if (layerId !== undefined) {
            e.originalEvent.preventDefault()
            e.originalEvent.stopPropagation()
            onFloorClick(layerId)
          }
        }
      }

      map.on('click', handleClick)
    },

    onRemove() {
      if (handleMouseMove) {
        map.off('mousemove', handleMouseMove)
      }
      if (handleClick) {
        map.off('click', handleClick)
      }
      if (handleContextMenu) {
        map.getCanvas().removeEventListener('contextmenu', handleContextMenu)
      }
    },

    render(_gl, args) {
      const rotationX = new THREE.Matrix4().makeRotationAxis(
        new THREE.Vector3(1, 0, 0),
        Math.PI / 2,
      )
      const rotationZ = new THREE.Matrix4().makeRotationAxis(
        new THREE.Vector3(0, 0, 1),
        rotation,
      )

      const m = new THREE.Matrix4().fromArray(
        args.defaultProjectionData.mainMatrix,
      )
      const l = new THREE.Matrix4()
        .makeTranslation(mercator.x, mercator.y, mercator.z!)
        .scale(
          new THREE.Vector3(
            meterScale * scale,
            -meterScale * scale,
            meterScale * scale,
          ),
        )
        .multiply(rotationX)
        .multiply(rotationZ)

      const projectionMatrix = m.multiply(l)
      camera.projectionMatrix.copy(projectionMatrix)
      camera.projectionMatrixInverse.copy(projectionMatrix).invert()
      lastProjectionMatrix = projectionMatrix.clone()

      if (modelLoaded) {
        floorMaterials.forEach((material, mesh) => {
          const isHovered = mesh === hoveredMesh
          material.color.copy(isHovered ? HIGHLIGHT_COLOR : FLOOR_COLOR)
          material.opacity = isHovered ? 0.5 : 0
          material.needsUpdate = true
        })
      }

      renderer.resetState()
      renderer.render(scene, camera)
    },
  }
}
